import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { FadeUp, PageHeader } from '../components/Reveal';

const About = () => {
    const { t } = useLanguage();

    return (
        <main data-testid="about-page">
            <PageHeader
                kicker="Robin Spencer"
                title={t.about.title}
                sub={t.about.intro}
            />

            {/* Portrait + manifesto paragraph */}
            <section className="px-6 md:px-12 pb-24 md:pb-32 max-w-[1400px] mx-auto" data-testid="manifesto-section">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 items-start">
                    <FadeUp className="md:col-span-4">
                        <figure className="max-w-[360px]" data-testid="about-portrait">
                            <div className="overflow-hidden spotlight">
                                <img
                                    src="/images/robin-portrait.jpg"
                                    alt={t.about.portraitAlt}
                                    className="w-full aspect-[4/5] object-cover transition-transform duration-[1200ms] ease-[cubic-bezier(0.22,1,0.36,1)] hover:scale-105"
                                    loading="lazy"
                                />
                            </div>
                            <figcaption className="mt-4 text-xs tracking-[0.25em] uppercase text-fog">{t.about.portraitCaption}</figcaption>
                        </figure>
                    </FadeUp>
                    <FadeUp delay={0.15} className="md:col-span-8 md:pt-4">
                        <p
                            className="font-serif text-xl md:text-2xl leading-[1.6] tracking-tight text-paper text-justify hyphens-auto"
                            data-testid="about-paragraph"
                        >
                            {t.about.paragraph}
                        </p>
                    </FadeUp>
                </div>
            </section>

            {/* Painter-at-work photos */}
            <section className="px-6 md:px-12 pb-32 md:pb-44 max-w-[1400px] mx-auto">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12">
                    <FadeUp>
                        <figure data-testid="about-photo-1">
                            <div className="overflow-hidden spotlight">
                                <img
                                    src="/images/art-02.jpg"
                                    alt={t.about.photoCaption1}
                                    className="w-full aspect-[4/3] object-cover transition-transform duration-[1200ms] ease-[cubic-bezier(0.22,1,0.36,1)] hover:scale-105"
                                    loading="lazy"
                                />
                            </div>
                            <figcaption className="mt-4 text-xs tracking-[0.2em] uppercase text-fog">{t.about.photoCaption1}</figcaption>
                        </figure>
                    </FadeUp>
                    <FadeUp delay={0.1} className="md:mt-16">
                        <figure data-testid="about-photo-2">
                            <div className="overflow-hidden spotlight">
                                <img
                                    src="/images/art-03.jpg"
                                    alt={t.about.photoCaption2}
                                    className="w-full aspect-[4/3] object-cover transition-transform duration-[1200ms] ease-[cubic-bezier(0.22,1,0.36,1)] hover:scale-105"
                                    loading="lazy"
                                />
                            </div>
                            <figcaption className="mt-4 text-xs tracking-[0.2em] uppercase text-fog">{t.about.photoCaption2}</figcaption>
                        </figure>
                    </FadeUp>
                </div>
            </section>

            {/* CTA */}
            <section className="px-6 md:px-12 pb-32 md:pb-44 max-w-[1400px] mx-auto">
                <FadeUp>
                    <div className="relative overflow-hidden bg-surface border border-white/10 px-8 md:px-16 py-16 md:py-24" data-testid="about-cta">
                        <div className="spotlight absolute inset-0" aria-hidden />
                        <div className="relative flex flex-col md:flex-row md:items-end md:justify-between gap-10">
                            <h2 className="font-serif text-4xl md:text-5xl tracking-tighter text-paper max-w-lg">
                                {t.home.featuredTitle}
                            </h2>
                            <Link
                                to="/gallery"
                                className="group inline-flex w-fit items-center gap-3 rounded-full bg-paper px-7 py-3.5 text-sm font-medium text-ink transition-colors duration-300 hover:bg-dusk"
                                data-testid="about-gallery-cta"
                            >
                                {t.hero.ctaGallery}
                                <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
                            </Link>
                        </div>
                    </div>
                </FadeUp>
            </section>
        </main>
    );
};

export default About;
